import { useEffect, useState } from "react"
import { DropdownButton, IconButton, StandardButton } from "../atoms/buttons"
import { IconAtom } from "../atoms/iconAtom"
import { InputForm, TextAreaForm } from "../atoms/input"
import { Progress } from "../atoms/progress"
import { StandardComp } from "../atoms/standardComp"
import { TextAtom } from "../atoms/TextAtom"
import { TitleAtom } from "../atoms/titleAtom"
import { HorizontalLayout, VerticalLayout } from "../components/layout"
import { getAPI, postAPI } from "../api/api"
import { Spinner } from "../atoms/spinner"
import { Chip } from "../molecule/Chip"
import { Modal, ModalBody, ModalFooter, ModalTitle } from "../components/modal"
import Checkbox from "../atoms/checkboxnew"

const getRuleId = (rule, groupIndex, ruleIndex) => rule?.id || `${groupIndex}-${ruleIndex}`
const createRuleState = (groups = []) => groups.reduce((acc, group, groupIndex) => {
    (group?.items || []).forEach((rule, ruleIndex) => {
        const id = getRuleId(rule, groupIndex, ruleIndex)
        acc[id] = { active: rule?.active !== false, value: rule?.value ?? "" }
    })
    return acc
}, {})

export const InitCompany = ({ goDashboard }) => {
    const [screen, setScreen] = useState(0)
    const [direction, setDirection] = useState(1)
    const [loading, setLoading] = useState(false)
    const [warning, setWarning] = useState(null)
    const [fitRuleGroups, setFitRuleGroups] = useState([])
    const stepInfo = [{ description: "Website analyse en profiel aanmaak" }, { description: "Ideale lead criteria" }, { description: "Het profiel van uw bedrijf" }]

    const goNext = () => {
        setDirection(1)
        setScreen((oldScreen) => Math.min(oldScreen + 1, stepInfo.length - 1))
    }
    const goBack = () => {
        setDirection(-1)
        setScreen((oldScreen) => Math.max(oldScreen - 1, 0))
    }
    const handleDeepSearch = async (url) => {
        setLoading(true)
        const search = await postAPI(`api/business/info/create`, { url })
        if (search.status == "ok") {
            setFitRuleGroups(search?.data?.categories || [])
            setDirection(1)
            setScreen(1)
        } else {
            setWarning(true)
        }
        setLoading(false)
    }

    return (
        <VerticalLayout className="body-screen standard-padding" style={{ boxSizing: "border-box", alignItems: "center" }}>
            <VerticalLayout className={"init-screen"} style={{ gap: "1rem" }}>
                <InitStepNav step={screen + 1} maxStep={stepInfo.length} canGoBack={screen > 0} goBack={goBack} />
                <div key={`${screen}-${direction}`} className={`init-step-view ${direction < 0 ? "is-back" : "is-next"}`}>
                    <InitSetupComp groups={fitRuleGroups} goNext={goNext} finish={goDashboard} isLoading={loading} search={handleDeepSearch} screen={screen} warning={warning} />
                </div>
            </VerticalLayout>
        </VerticalLayout>
    )
}

const InitSetupComp = ({ groups, screen, search, isLoading, finish, goNext, warning }) => {
    const [fitRules, setFitRules] = useState(() => createRuleState(groups))
    useEffect(() => {
        setFitRules(createRuleState(groups))
    }, [groups])
    const submitRules = async () => {
        const items = groups.flatMap((group, groupIndex) => (group.items || []).map((rule, ruleIndex) => {
            const id = getRuleId(rule, groupIndex, ruleIndex)
            return {
                id,
                active: !!fitRules[id]?.active,
                value: fitRules[id]?.value ?? "",
                description: rule?.description || rule?.sub || ""
            }
        })).filter((item) => item.active && `${item.value}`.trim() !== "").map(({ active, ...item }) => item)
        await postAPI(`api/business/preferred-company-info/create`, items)
        goNext()
    }

    if (screen === 0) return <WebsiteSearchComp warning={warning} search={search} isLoading={isLoading} />
    if (screen === 1) return <FitRulesComp groups={groups} fitRules={fitRules} setFitRules={setFitRules} goNext={submitRules} />
    return (
        <>
            <SummaryComp />
            <StandardButton onClick={finish} className={"heavy-shadow"} style={{ width: "100%", margin: 0, textAlign: "center", justifyContent: "center", alignItems: "center", fontWeight: "bold", fontSize: "1.2rem" }} title={"Klaar om te starten"} />
        </>
    )
}

const FitRulesComp = ({ groups, fitRules, setFitRules, goNext }) => {
    const setRule = (id, key, value) => setFitRules((oldRules) => ({ ...oldRules, [id]: { ...oldRules[id], [key]: value } }))
    const changeNumber = (rule, id, diff) => {
        const currentValue = Number(fitRules[id]?.value || rule?.value || 0)
        const nextValue = Math.max(rule?.min || 0, currentValue + diff)
        setRule(id, "value", String(nextValue))
    }
    if (!groups.length) return (
        <StandardComp editClass={"heavy-shadow"} className="fit-rules-card" style={{ marginRight: "0", padding: "2rem" }}>
            <VerticalLayout style={{ width: "unset", gap: "1rem", alignItems: "center" }}>
                <TitleSub title={"Geen vragen gevonden"} sub={"Er zijn geen kwalificatievragen teruggekomen uit de analyse."} />
                <StandardButton onClick={goNext} style={{ width: "100%", margin: 0, textAlign: "center", justifyContent: "center", alignItems: "center", fontWeight: "bold", fontSize: "1.2rem" }} title={"Doorgaan"} />
            </VerticalLayout>
        </StandardComp>
    )
    return (
        <StandardComp editClass={"heavy-shadow"} className="fit-rules-card" style={{ marginRight: "0", padding: "2rem" }}>
            <VerticalLayout style={{ width: "unset", gap: "1rem" }}>
                <TitleSub title={"Geef aan waar jouw bedrijven aan moeten voldoen"} sub={"Selecteer welke criteria belangrijk zijn en vul ze in."} />
                {groups.map((group, groupIndex) => (
                    <VerticalLayout key={`${group?.category || "group"}-${groupIndex}`} className="fit-rule-section">
                        <h3 className="fit-rule-section-title">{((group?.category || "").split(":")[0] || `Categorie ${groupIndex + 1}`).trim()}</h3>
                        <ul className="fit-rule-list">
                            {(group?.items || []).map((rule, ruleIndex) => {
                                const id = getRuleId(rule, groupIndex, ruleIndex)
                                const isActive = !!fitRules[id]?.active
                                const value = fitRules[id]?.value ?? ""
                                const options = Array.isArray(rule?.options) ? rule.options : []
                                return (
                                <li key={id} className={`fit-rule-item ${!isActive ? "is-disabled" : ""}`}>
                                    <label className="fit-rule-label">
                                        <Checkbox checked={isActive} onChange={(e) => setRule(id, "active", e.target.checked)} />
                                        <VerticalLayout style={{ width: "unset", gap: ".2rem" }}>
                                            <span className="fit-rule-title">{rule?.label || "Voorwaarde"}</span>
                                            <span className="fit-rule-sub">{rule?.sub || ""}</span>
                                        </VerticalLayout>
                                    </label>
                                    <HorizontalLayout className="fit-rule-value" style={{ width: "unset", gap: ".7rem", alignItems: "center" }}>
                                        {rule?.type === "number" ? (
                                            <HorizontalLayout className="fit-rule-stepper" style={{ width: "unset", gap: "1.2rem", alignItems: "center" }}>
                                                <IconButton icon2={"minus"} size={"1.1rem"} className={"fit-rule-stepper-btn"} onClick={() => changeNumber(rule, id, -(rule?.step || 1))} style={{ opacity: !isActive ? .4 : 1, pointerEvents: !isActive ? "none" : "auto" }} />
                                                <span className="fit-rule-stepper-value">{value || rule?.value || 0}</span>
                                                <IconButton icon2={"plus"} size={"1.1rem"} className={"fit-rule-stepper-btn"} onClick={() => changeNumber(rule, id, rule?.step || 1)} style={{ opacity: !isActive ? .4 : 1, pointerEvents: !isActive ? "none" : "auto" }} />
                                            </HorizontalLayout>
                                        ) : rule?.type === "text" ? (
                                            <InputForm title={""} style={{ margin: 0 }} value={value} placeholder={rule?.placeholder || ""} disabled={!isActive} typeInput={(text) => setRule(id, "value", text)} />
                                        ) : (
                                            <DropdownButton
                                                disabled={!isActive}
                                                content={value || "Selecteer"}
                                                classButton={"fit-rule-control fit-rule-dropdown"}
                                                classContent={"fit-rule-dropdown-menu"}
                                                dropdown={options.map((option) => ({ id: option, content: option, selected: value === option }))}
                                                optionSelected={(selectedValue) => setRule(id, "value", selectedValue)}
                                            />
                                        )}
                                        {!!rule?.suffix && <span className="fit-rule-unit">{rule.suffix}</span>}
                                    </HorizontalLayout>
                                </li>
                            )})}
                        </ul>
                    </VerticalLayout>
                ))}
                <StandardButton onClick={goNext} style={{ width: "100%", margin: 0, textAlign: "center", justifyContent: "center", alignItems: "center", fontWeight: "bold", fontSize: "1.2rem" }} title={"Afronden"} />
            </VerticalLayout>
        </StandardComp>
    )
}

const InitStepNav = ({ step, maxStep, canGoBack, goBack }) => (
    <StandardComp editClass={"heavy-shadow"} className="init-step-nav" style={{ marginRight: 0 }}>
        <HorizontalLayout style={{ justifyContent: "space-between", alignItems: "center" }}>
            <button className={`init-back-btn ${!canGoBack ? "disabled" : ""}`} disabled={!canGoBack} onClick={goBack}>
                <IconAtom icon2={"arrow-left"} color={canGoBack ? "var(--primary-600)" : "var(--grey-400)"} />
            </button>
        </HorizontalLayout>
        <Progress round={true} height="0.4rem" percentage={Math.round(step / maxStep * 100)} />
    </StandardComp>
)

const WebsiteSearchComp = ({ search, isLoading, warning }) => {
    const [url, setUrl] = useState("")
    return (
        <StandardComp editClass={"heavy-shadow"} style={{ marginRight: "0", padding: "5rem" }}>
            <VerticalLayout style={{ alignItems: "center", gap: "1rem" }}>
                <IconAtom iconSize={50} icon2={"globe"} color={"var(--primary-600)"} />
                <TitleSub title={"Laten we wat meer te weten komen over je bedrijf"} sub={"Met je website krijgen we snel inzicht in bedrijfsdoelen, producten en branding."} />
                {!isLoading && <InputForm typeInput={setUrl} value={url} placeholder={"https://www.voorbeeld.nl/"} title={"Website url"} />}
                {warning && <TextAtom style={{ color: "var(--error-500)" }} title={"Website kon niet worden geanalyseerd. Probeer een andere url."} />}
                {isLoading ? <Spinner size={50} /> : <StandardButton style={{ width: "100%", justifyContent: "center", alignItems: "center", fontWeight: "bold", marginLeft: "0" }} onClick={() => search(url)} title={"Doorzoek website"} />}
                <HorizontalLayout style={{ alignItems: "center", width: "unset", gap: "1rem" }}>
                    <Secure icon={"shield"} title={"Privacy"} />
                    <Secure icon={"clock"} title={"Minder dan 30 sec"} />
                </HorizontalLayout>
            </VerticalLayout>
        </StandardComp>
    )
}

export const SummaryComp = () => (
    <VerticalLayout>
        <SummaryModule />
    </VerticalLayout>
)

const SummaryModule = () => {
    const [profile, setProfile] = useState({ goals: [], branding: [], summary: "", opportunities: "" })
    const [activeModal, setActiveModal] = useState(false)
    useEffect(() => { loadProfile() }, [])
    const loadProfile = async () => {
        const loadedProfile = await getAPI(`api/business/detailed-info`)
        setProfile(loadedProfile.data)
    }
    return (
        <StandardComp editClass={"heavy-shadow"} style={{ margin: 0, height: "20rem" }}>
            <HorizontalLayout style={{ width: "unset", alignItems: "flex-start", gap: "3rem", height: "100%" }}>
                <VerticalLayout style={{ gap: "1rem", height: "unset" }}>
                    <HorizontalLayout>
                        <TitleAtom title={"Bedrijfsoverzicht"} />
                        <EditComp editNow={() => setActiveModal(true)} />
                    </HorizontalLayout>
                    <HorizontalLayout>
                        <ProfileModule chips={profile?.goals || []} title={"DOELEN"} />
                        <ProfileModule chips={profile?.branding || []} title={"BRANDING"} />
                    </HorizontalLayout>
                    <ExtraInfoModule title={"Samenvatting"} text={profile.summary} />
                    <ExtraInfoModule title={"Kansen"} text={profile.opportunities} />
                </VerticalLayout>
            </HorizontalLayout>
            <EditModal data={profile} close={() => setActiveModal(false)} active={activeModal} />
        </StandardComp>
    )
}

const EditModal = ({ active, data, close }) => {
    const [textValue, setTextValue] = useState({ summary: data?.summary, opportunities: data?.opportunities })
    return (
        <Modal visible={active}>
            <ModalTitle title={"Pas aan"} />
            <ModalBody>
                <TextAreaForm title={"Samenvatting"} value={textValue?.summary || data.summary} typeInput={(text) => setTextValue({ ...textValue, summary: text })} />
                <TextAreaForm title={"Kansen"} value={textValue?.opportunities || data.opportunities} typeInput={(text) => setTextValue({ ...textValue, opportunities: text })} />
            </ModalBody>
            <ModalFooter>
                <StandardButton title={"Opslaan"} onClick={close} />
            </ModalFooter>
        </Modal>
    )
}

const EditComp = ({ editNow }) => (
    <HorizontalLayout style={{ width: "unset", gap: "0.3rem" }} onClick={editNow}>
        <IconAtom color={"var(--primary-600)"} size={12} icon2={"edit"} />
        <TextAtom style={{ fontSize: "0.7rem", color: "var(--primary-600)", width: "3rem", fontWeight: "bold" }} title={"Pas aan"} />
    </HorizontalLayout>
)

const ProfileModule = ({ title, chips }) => (
    <VerticalLayout>
        <TextAtom style={{ color: "grey", fontSize: "0.8rem" }} title={title} />
        {chips.map((chip, index) => <Chip key={`${title}-${index}`} style={{ backgroundColor: "var(--primary-100)", width: "max-content" }} disabled={false} label={chip.title} />)}
    </VerticalLayout>
)

const ExtraInfoModule = ({ title, text }) => (
    <VerticalLayout>
        <TextAtom style={{ fontWeight: "bold" }} title={title} />
        <TextAtom style={{ color: "grey" }} title={text} />
    </VerticalLayout>
)

const Secure = ({ icon, title }) => (
    <HorizontalLayout style={{ alignItems: "center", width: "unset", gap: "0.2rem", justifyContent: "center" }}>
        <IconAtom color={"var(--grey-600)"} iconSize={13} icon2={icon} />
        <TextAtom style={{ textAlign: "center", color: "var(--grey-600)", fontSize: "0.8rem" }} title={title} />
    </HorizontalLayout>
)

const TitleSub = ({ title, sub }) => (
    <>
        <TitleAtom style={{ textAlign: "center" }} title={title} />
        <TextAtom style={{ textAlign: "center", color: "var(--grey-600)", fontSize: "0.8rem" }} title={sub} />
    </>
)
